// noinspection JSUnusedGlobalSymbols

import {
  ChangeEventHandler,
  CSSProperties,
  KeyboardEventHandler,
  MouseEventHandler,
  MutableRefObject,
  ReactFragment,
  ReactNode
} from "react"
import { PathRouteProps } from "react-router"
import { RootState } from "../store"
import { ANALYSIS_FILTER, INVITATION_STATUS, RELEASE_FILTER, TABLE_COLUMN_TYPE, USER_ROLE } from "../constants"
import { FieldInputProps, FieldMetaState, FieldRenderProps } from "react-final-form"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { Method } from "axios"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import { SetURLSearchParams } from "react-router-dom"

export type LogoProps = {
  colorClass: string
}

export type SlackChannelMember = {
  id: string
  displayName: string
  realName?: string
}

export type InputProps = {
  id?: string
  type?: string
  label?: string
  className?: string
  inputClass?: string
  placeholder?: string
  autoComplete?: string
  value?: string | ReadonlyArray<string> | number
  onChange?: ChangeEventHandler<HTMLInputElement>
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  checked?: boolean
  maxLength?: number
  disabled?: boolean
  pattern?: string
  containerStyle?: CSSProperties
  onClick?: MouseEventHandler<HTMLInputElement>
}

export type ToggleProps = {
  id?: string
  name?: string
  label?: string
  className?: string
  inputClass?: string
  labelClass?: string
  value?: string | ReadonlyArray<string> | number | boolean
  onChange?: ChangeEventHandler<HTMLInputElement>
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  checked?: boolean
  disabled?: boolean
}

export type ButtonProps = {
  type?: "link" | "button"
  size?: "sm" | "md" | "lg"
  to?: string
  text?: string | ReactFragment
  className?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  light?: boolean
  disabled?: boolean
  buttonType?: "button" | "submit"
}

export interface FormValues {}

export class PaginatedData<DataType> {
  results: DataType[] = []
  count: number | undefined = undefined
  next = ""
  previous = ""
}

export abstract class FormProps<FormType> {
  onSubmit!: (formValues: FormValues) => void
  validate?: (formValues: FormType) => void
  onDelete?: (id: number) => void
  initialValues: FormType
  editable? = false
}

export class LoginFormValues implements FormValues {
  username?: string
  password?: string
  email?: string
  ipAddress?: string
  code?: string
  turnstileToken?: string
}

export class FormPageProps<FormType extends Object> {
  onSubmit!: (formValues: FormType, callback?: Function) => void
  login?: (formValues: LoginFormValues, callback: () => void) => Promise<void>
  initialValues?: FormType
}

export class LoginPageProps {
  login!: (formValues: LoginFormValues, callback?: () => void) => void
  emailValidation: (
    form: LoginFormValues,
    userCheck: boolean,
    useCredentials: boolean,
    subject: string,
    callback?: (overideCodeValidation: boolean, enabled2FA: boolean) => void
  ) => void
  validateCode: (form: LoginFormValues, callback?: () => void) => void
}

export class ForgotPasswordFormValues implements FormValues {
  email? = ""
  ipAddress? = ""
}

export class ResetPasswordFormValues implements FormValues {
  email? = ""
  code? = ""
  password? = ""
  repeatedPassword? = ""
}

export class CreatePasswordFormValues implements FormValues {
  email? = ""
  code? = ""
  password? = ""
  repeatedPassword? = ""
}

export type ChangePasswordFormProps = {
  username: string
  changePassword: (formValues: ChangePasswordFormValues) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export class ChangePasswordFormValues {
  username = ""
  currentPassword = ""
  newPassword = ""
  newPasswordAgain = ""
}

export type RootProps = {
  children?: ReactNode
  initialState?: RootState
}

export class InputRenderProps implements FieldRenderProps<any> {
  input!: FieldInputProps<any>
  meta!: FieldMetaState<any>
  placeholder?: string
  className?: string
  inputClass?: string
  type?: string
  label?: string
  inlineElement?: ReactFragment
}

export class ToggleRenderProps implements FieldRenderProps<any> {
  input!: FieldInputProps<any>
  meta!: FieldMetaState<any>
  inputClass?: string
  labelClass?: string
  className?: string
  label?: string
}

export class SearchSelectRenderProps implements FieldRenderProps<any> {
  input!: FieldInputProps<any>
  meta!: FieldMetaState<any>
  placeholder?: string
  className?: string
  label?: string
  loading?: boolean
  multiple?: boolean
  onSelect?: ChangeEventHandler<HTMLInputElement>
}

export type LoginLayoutProps = {
  title?: string
  children?: ReactNode
}

export type CardProps = {
  className?: string
}

export type HeaderNavigationItem = {
  name: string
  href?: string
  current: () => boolean
  permittedRoles?: USER_ROLE[]
  type: "link" | "dropdown"
  items?: ReactNode[]
}

export type HeaderDropDownItem = {
  link: ReactNode
  permittedRoles: USER_ROLE[]
}

export type DropdownMenuProps = {
  items: HeaderDropDownItem[]
  userRole: number
}

export type AccountNavigationItemProps = {
  path: string
  icon?: ReactNode
  title: string
  description: string
}

export type AccordionProps = {
  title: string | ReactFragment
  content: string | ReactFragment
  alwaysOn?: boolean
}

export type ArrowProps = {
  rotate?: number
}

export interface RouteProps extends PathRouteProps {
  private: boolean
  redirect?: string
  permittedRoles?: USER_ROLE[]
  permittedAdminPermissons?: string[]
  overrideDefaultRoute?: boolean
}

export type PrivateRouteProps = {
  private: boolean
  loggedIn: boolean
  redirect?: string
  userRole: number
  adminPermissions: string
  permittedRoles?: USER_ROLE[]
  permittedAdminPermissons?: string[]
  overrideDefaultRoute?: boolean
}

export interface Action {
  type: string
  payload: any
}

export interface AdminPermission {
  id: number
  name: string
  group: string
  code: string
}

export class User {
  id: number | undefined = undefined
  firstName = ""
  lastName = ""
  email = ""
  username = ""
  isSuperuser = false
  photo = ""
  phone = ""
  preferredCurrency = "TRY"
  roleId = 0
  permissionUser: any[]
  affiliateUserId: number | undefined = undefined
  affiliateCommission: number | undefined = undefined
  appliedAffiliateUser: User | undefined = undefined
  role?: { id: number; name: string }
  productionHolder: string
  copyrightHolder: string
  lcUsername: string
  affiliateCount: number = 0
  userExpense?: UserExpense
  bmwCustomId: string
  bio = ""
  stageName = ""
  userCredit?: UserCredit
  validateEmail = true
  genre = ""
  marketingId: string | undefined = undefined
  company = ""
  artistSpotifyId = ""
  label = ""
  totpEnabled = false
  adminPermissions = ""
  userMembers: User[] = []
  excludeFromProjection = false
  hideReportTotalAmount = true
  userTakedown?: UserTakedown
  advanceAccount: boolean = false
  type: string = ""
  isUserMember: boolean = false
}

export type SaveActionResult<T> = {
  operation: "created" | "updated"
  model: T
}

export class AuthUser extends User {
  token: string
}

export type AuthState = {
  loggedIn: boolean
  user: User
  token?: string | null
}

export type PublicState = {
  invitationStatus?: InvitationStatus
}

export type ProfileMenuProps = {
  logout: (callback: () => void) => void
  loggedIn: boolean
  user: User
  isAdmin?: boolean
  setChangeUserModalOpen: (open: boolean) => void
  goBackToAccount: () => void
}

export type NavigationProps = {
  user: User
  fetchUserData: () => Promise<void>
  missingTrackShareHolderCount: number
  fetchMissingTrackShareHolderCount: () => Promise<void>
  releaseEditAgainSubmitCount: number
  fetchReleaseEditAgainSubmitCount: () => Promise<void>
}

export class TableColumn {
  header? = ""
  headerStyle?: CSSProperties
  cellStyle?: CSSProperties
  type? = TABLE_COLUMN_TYPE.TEXT
  attribute? = ""
  datePattern? = ""
  dateParsePattern? = ""
  constType?: any
  render?: (row: any) => ReactNode
  sortDirection?: "asc" | "desc"
  sortable?: boolean
  sortFilterField? = ""
  filter?: boolean
  renderOnlyWhenHovered?: boolean
  overrideHeader?: () => ReactNode
}

export type TableProps = {
  hidden?: boolean
  title: ReactNode | string
  columns: TableColumn[]
  data: any[]
  className?: string
  minRowCount?: number
  rowHeightInPx?: number
  size?: "sm" | "md" | "lg"
  pagination?: boolean
  count?: number
  limit?: number
  onClickPage?: (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean },
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => void
  topRightElement?: ReactNode
  onSelectRow?: (index: number) => void
  loading?: boolean
  setTableAPI?: (tableAPI: TableAPI) => void
  onSortOrFilterChange?: (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => Promise<void>
  additionalFilters?: any
  inactiveRows?: any[]
  isAdmin?: boolean
  selectedUserId?: number
  approveDisabled?: boolean
  platform?: string
  openSidebar?: boolean
  setOpenSidebar?: (openSidebar: boolean) => void
  topFilters?: ReactNode
  selectedAccountType?: string
  user?: User
}

export type TableNewProps = {
  hidden?: boolean
  title: ReactNode | string
  columns: TableColumn[]
  data: any[]
  className?: string
  minRowCount?: number
  rowHeightInPx?: number
  size?: "sm" | "md" | "lg"
  pagination?: boolean
  count?: number
  limit?: number
  onClickPage?: (
    pageNumber: number,
    filters?: { [key: string]: string | number | boolean },
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => void
  topRightElement?: ReactNode
  onSelectRow?: (index: number, row?: any) => void
  loading?: boolean
  setTableAPI?: (tableAPI: TableAPI) => void
  onSortOrFilterChange?: (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => Promise<void>
  additionalFilters?: any
  inactiveRows?: any[]
  isAdmin?: boolean
  selectedUserId?: number
  approveDisabled?: boolean
  platform?: string
  openSidebar?: boolean
  setOpenSidebar?: (openSidebar: boolean) => void
  topFilters?: ReactNode
  notFoundTitle?: string
  notFoundDesc?: string
  onMouseEnterRow?: (row: any, index: number) => void //row: data
  onMouseLeaveRow?: (row: any, index: number) => void // row: data
  hoveredRowId?: number
  sortParamsReader?: (params: SortParams) => void
  pageNumber?: number
  overrideSortParams?: boolean
  sortParamsValue?: SortParams
  overrideTableOverflow?: string
  paginationKey?: string
  doNotApplyAdditionalFilters?: boolean
  paginationValidation?: boolean
  tablePaddingTop?: number
  freezeHeader?: boolean
}

export class SortParams {
  field = ""
  direction?: "asc" | "desc"
}

export type TableAPI = {
  currentPage: number
  setCurrentPage: (page: number) => void
}

export class YoutubeChannel {
  id: number | undefined = undefined
  channelId = ""
  name = ""
}

export class YoutubeVideo {
  id: number | undefined = undefined
  channel: number | undefined = undefined
  assetId: ""
  channelDetail = new YoutubeChannel()
  customId = ""
  isVerified = false
  isrc = ""
  title = ""
  trackId: number | undefined = undefined
  trackDetail = new AdminTrack()
  trackIsrc = ""
  videoId = ""
  mainOwnerShare = new MainOwnerShareData()
}

export class MainOwnerShareData {
  user = ""
  share: number | undefined = undefined
}

export class Track {
  id: number | undefined = undefined
  release: number | undefined = undefined
  artists: number[] = []

  artistsList: Artist[] = []
  releaseDetail = new Release()
  mainOwnerShare = new MainOwnerShareData()
  shareData?: { length: number; roles: string[]; share: number }
  shareHolders?: { id: number; roleList: UserTrackShareRole[]; share: number; userId: number; userName: string }[] = []

  title = ""
  isrc = ""
  iswc = ""
  audioDuration: ""
  audioFileUrl: ""
  audioLanguage: ""
  number: ""
  parentalAdvisory: false
  recordingPlace: ""
  genre = ""
  copyright = ""
  metadataLanguage = ""

  producer = ""
  author = ""
  composer = ""
  arranger = ""
  contributor = ""
  remixer = ""
  featuring = ""
  lyricist = ""
  lyrics = ""
  archived = false
}

// It does not mean the track belongs to Admin, it is created to use only in Admin side(Admin panel)
export class AdminTrack {
  id: number | undefined = undefined
  release_id: number | undefined = undefined
  artists: number[] = []

  artistList: Artist[] = []
  release = new Release()
  mainOwnerShare = new MainOwnerShareData()
  shareData?: { length: number; roles: string[]; share: number }
  shareHolders?: { id: number; roleList: UserTrackShareRole[]; share: number; userId: number; userName: string }[] = []

  title = ""
  isrc = ""
  iswc = ""
  audioDuration: ""
  audioFileUrl: ""
  audioLanguage: ""
  number: ""
  parentalAdvisory: false
  recordingPlace: ""
  genre = ""
  copyright = ""
  metadataLanguage = ""

  producer = ""
  author = ""
  composer = ""
  arranger = ""
  contributor = ""
  remixer = ""
  featuring = ""
  lyricist = ""
  lyrics = ""
}

export type TrackShareHolder = {
  id: number
  title: string
  share: number
  track: Track
  trackId: number
  user: User
  userId: number
  status: string
}

export type ReleaseUser = {
  id: number
  release: Release
  releaseId: number // or Release type if you have it defined
  user: User
  userId: number
}

export type Contributor = {
  [key: string]: string
}

export type MissingTrackShareHolder = {
  id: number
  releaseId: number // or Release type if you have it defined
  isrc: string
  title: string
  trackShareHolderList: TrackShareHolder[]
  releaseShareHolderList: TrackShareHolder[]
  releaseUserList: ReleaseUser[]
  release?: Release
  contributors: Contributor
  number: number
}

export type MissingYoutubeVideo = {
  id: number
  videoId: string
  title: string
  label: string
  isrc: string
  channelId: string
  channelName: string
  assetId: string
}

export type BaseSummary = {
  id: number
  totalAmount: number
  totalNet: number
  value: string
  approved: boolean
  paymentApproved: boolean
  date: string
  transferDate: string
  currency: string
}

export type SummaryDuplicate = {
  user: User
  summaryList: BaseSummary[]
}

export type Setting = {
  id: number
  type: string
  name: string
  value: string
  description: string
  group: string
  updated: string
}

export type IntercomTriageKeyword = {
  id?: number
  category: string
  keyword: string
  created?: string
  updated?: string
}

export type IntercomTriageAssignee = {
  id?: number
  category: string
  slackMemberId: string
  created?: string
  updated?: string
}

export type State51Delivery = {
  id: number
  releaseId: number // or Release type if you have it defined
  userId: number // or User type if available
  created: string // ISO date string
  attempt: number
  status: string
  message?: string | null
  nextRun: string // ISO date string
  batchId?: string | null
  orderId?: string | null
  productId?: string | null
  label?: string | null
  genre?: string | null
  release?: Release
  user?: User
}

export class Release {
  id: number | undefined = undefined
  label: number | undefined = undefined
  publisher: number | undefined = undefined
  artists: number[] = []
  title = ""
  releaseDate = ""
  artistsList: Artist[] = []
  labelDetail = new Label()
  publisherDetail = new Publisher()
  coverFileUrl = ""
  version = ""
  upc = ""
  platforms = ""
  status = ""
  type = ""
  production = ""
  labelCamp: boolean = false
  productionYear = ""
  catalogNumber = ""
  tracksList?: Track[] = []
  productId?: string
  genre: string = ""
  totalTrack: number = 0
  appleDigitalMaster: string = ""
  urgent: boolean = false
  smartLink?: string
  state51Id?: string
  marketingId?: string
  driveId?: string //BMV Drive Folder Id
  mesamMsgId?: string // PUBLISHING Drive Folder Id
  editorialPitchingId?: string // EDITORIAL PITCHING Drive Sheet Id
  forceUploadType?: string
  takedown: boolean = false
  releaseMarketing?: ReleaseMarketing
  featuring?: string[]
  state51Delivery?: State51Delivery
  editStatus: string = ""
}

export class ReleaseMarketing {
  id: number
  release?: Release
  releaseId?: number
  user?: User
  userId?: number
  created?: string
  image1?: string
  image2?: string
  image3?: string
  video1?: string
  video2?: string
  pressRelease?: string
}

export class PaymentHistoryDetail {
  id: number | undefined = undefined
  period = ""
  revenue: number | undefined = undefined
  totalPayment: number | undefined = undefined
  paymentDay: number[] = []
}

export class SocialMedia {
  id?: number
  userId?: number
  url?: string
  created?: string
  actionCount?: number
  icon?: string
}

export class Event {
  id?: number
  userId?: number
  url?: string
  created?: string
  actionCount?: number
  startDate?: Date
  endDate?: Date
  title?: string
  address?: string
}

export type ReleaseDetailProps = {
  releases: Release[]
  fetchReleaseTracks: (limit: number, offset: number, releaseId: string, userId: number) => Promise<void>
  selectRelease: (release?: Release, releaseId?: string) => void
  updateReleaseEditStatus: (id: number, editStatus: string) => Promise<boolean>
  userId: number
  release: Release
  paginatedTracks: PaginatedData<Track>
  isAdmin: boolean
  shareData: CollaboratorData[]
  newReleasePermission: boolean
}

export type SmartLink = {
  id?: number
  release?: Release
  releaseId?: number
  viewCount?: number
  actionCount?: number
  urls?: SmartLinkUrl[]
}

export type SmartLinkUrl = {
  id?: number
  smartLinkId?: number
  url?: string
  created?: string
  icon?: string
}

export type SmartLinkPreSave = {
  id?: number
  spotifyEmail?: string
  validatedEmail?: string
  created?: string
  userId?: string
}

export type SmartLinkAnalytics = {
  viewCount?: number
  actionCount?: number
  preSaveCount?: number
  subscribeCount?: number
  preSaveList?: SmartLinkPreSave[]
}

export type ArtistSmartLinkProps = {
  user: User
  fetchUserData: () => Promise<void>
}

export type ReleaseSmartLinkProps = {
  release: Release
  releases: Release[]
  selectRelease: (release?: Release, releaseId?: string) => void
}

export type LCReleaseFormDataDetail = {
  id?: string
  attributes: { [key: string]: any }
  relationships: { [key: string]: any }
  type: string
}

export type LCReleaseFormData = {
  data: LCReleaseFormDataDetail
}

export type SpotifyEditorialPitching = {
  id?: number
  trackIsrc?: string
  releaseId?: number
  genre: string[]
  mood: string[]
  style: string[]
  instrumentation: string[]
  description: string
}

export type LabelCampBulkTrackUpload = {
  id: number
  productId: number
  status: string
  progress: string
  created: string
}

export type LCReleaseForm = {
  product: LCReleaseFormData
  offer: LCReleaseFormData
  tracks: LCReleaseFormData[]
  track_offers: LCReleaseFormData[]
  import_tasks: LCReleaseFormData[]
  import_sub_tasks: LCReleaseFormData[]
  records: LCReleaseFormData[]
  logs: LCReleaseFormData[]
  kahuna_release_id?: number
  takedown?: boolean
}

export type ResidualFormProps = {
  sendToBeatport: boolean
}

export type EditReleaseProps = {
  userId: number
  release: Release
}

export type AssetRegistryProps = {
  user: User
  release: Release
  selectRelease: (release?: Release, releaseId?: string) => void
  fetchUserData: () => Promise<void>
}

export type ReleaseRegistryProps = {
  user: User
  userId: number
  release: Release
  selectRelease: (release?: Release, releaseId?: string) => void
}

export type CatalogInfoProps = {
  pageTitle: string
  breadcrumbs: string
  coverPhoto: string
  title: string
  artists: Artist[]
  UPC: string
  status: string
  releaseDate: string
  onClickEdit?: (isEdit: boolean) => void
  genre?: string
  isrc?: string
}

export type UserExpense = {
  created?: string
  adminFee?: number
  distributionFee?: number
  distributionFeePostRecoup?: number
  user?: number
  taxType?: number
  preferredCurrency?: number
  paymentPeriod?: number
  publishingKahunaShare?: number
  paymentThreshold?: number
  id?: number
  preRecoupCommission?: number
  postRecoupCommission?: number
}

export type UserCredit = {
  id?: number
  created?: string
  subscriptionType?: string
  userId?: number
  enabled?: boolean
  urgentNewRelease?: number
  takedown?: number
}

export type UserAdvance = {
  id?: number
  userId?: number
  created?: string
  startDate?: string
  endDate?: string
  advanceTotal?: number
  currencyId?: number
  originalCurrencyId?: number
  currency?: number
  originalCurrency?: number
  paidTotal?: number
  feeTotal?: number
  advanceType?: string
}

export type TrackDetailProps = {
  track: Track
  release: Release
  selectTrack: (track?: Track, trackId?: string) => void
  selectRelease: (release?: Release, releaseId?: string) => Promise<void>
  trackShareData: CollaboratorData[]
  fetchTrackShareData: (trackId: number) => Promise<any>
  setSelectedTrack: (selectedTrack: number | undefined) => void
  selectedTrack: number | undefined
}

export type TrackInformationProps = {
  track: Track
}

export type ProfileProps = {
  saveUserData: (values: User) => void
  saveUserProfilePhoto: (photo: FormData) => Promise<void>
  user: User
}

export type PaymentFormProps = {
  initialValues?: PaymentMethod
  canDelete?: boolean
  editable?: boolean
  edit?: boolean
  setEdit?: React.Dispatch<React.SetStateAction<boolean>>
  adminPanelUserId?: number
  deletePaymentMethod: (id: number) => void
  updatePaymentMethod: (formValues: PaymentMethod) => void
  createPaymentMethod: (f: PaymentMethod, userId?: number) => void
  setAdding?: (adding: boolean) => void
  title?: string
  addPaymentMethod?: boolean
  setAddPaymentMethod?: React.Dispatch<React.SetStateAction<boolean>>
  chosenMethod?: PaymentMethod
}

export class PaymentMethod implements FormValues {
  id?: number
  user?: number
  bankName: string
  accountHolderName: string
  iban: string
  swiftCode?: string
  created?: string
  currency?: string
  accountNumber?: string
  accountType?: string
  bankCode?: string
  countryType?: string
  taxId?: string
}

export type PaymentPageProps = {
  adminPanelUserId?: number
  fetchPaymentMethods: (userId?: number) => void
  paymentMethods: Record<string, PaymentMethod>
  adminPage?: boolean
  user?: User
  readOnly?: boolean
  hideTitle?: boolean
}

export type CatalogFilter = {
  choosenStatus: string[]
  choosenGenre: string[]
  ownerStatus: string
}

export type CatalogProps = {
  paginatedReleases: PaginatedData<Release>
  loggedInUser?: User
  user: User
  fetchCatalogData: (
    limit: number,
    offset: number,
    userId: number,
    filter: CatalogFilter,
    search?: string,
    searchUpc?: string,
    searchArtist?: string,
    searchLabel?: string,
    searchIsrc?: string,
    startDate?: string,
    endDate?: string,
    sortField?: string,
    sortDirection?: "asc" | "desc",
    fetchField?: string,
    searchFeaturing?: string,
    isUserEditSelected?: boolean
  ) => Promise<any>
  selectCatalogFilter: (choosenStatus: string[], ownerStatus: string, choosenGenre: string[]) => Promise<any>
  selectedCatalogFilter: CatalogFilter
  newReleasePermission: boolean
  updateReleaseLabelCamp: (id: number, labelCampStatus: boolean) => Promise<boolean>
  updateReleaseStatus: (id: number, status: string) => Promise<boolean>
  updateReleaseEditStatus: (id: number, editStatus: string) => Promise<boolean>
  deleteRelease: (id: number) => Promise<boolean>
  releaseEditAgainSubmitCount: number
}

export class Artist {
  id: number | undefined = undefined
  name: string = ""
  spotifyId: string = ""
  cover: string = ""
}

export class Label {
  id: number | undefined = undefined
  name = ""
}

export class Publisher {
  id: number | undefined = undefined
  name = ""
}

export class CatalogData {
  paginatedReleases: PaginatedData<Release> = new PaginatedData<Release>()
  paginatedReleaseTracks: PaginatedData<Track> = new PaginatedData<Track>()
  selectedRelease: Release = new Release()
  selectedTrack: Track = new Track()
  trackShareData: CollaboratorData[] = []
  shareData: CollaboratorData[] = []
  selectedCatalogFilter: CatalogFilter = {
    choosenStatus: ["active", "draft", "reviewing"],
    ownerStatus: RELEASE_FILTER.ALL,
    choosenGenre: []
  }
}

export class TracksData {}

export type AdminUserProps = {
  paginatedUsers: PaginatedData<User>
  selectedUser: User
  fetchAllUsers: (
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => Promise<void>
  selectUser: (user: User) => void
  fetchLastUpdate: () => Promise<any>
  lastUpdate: string | null
  refreshLabelCamp: () => Promise<any>
  refreshReports: (date: string) => Promise<any>
  refreshSummaries: (date: string, userId: number) => Promise<any>
  loggedInUser?: User
  tab: number
  setTab: (tab: number) => void
  modalOption: number | undefined
  setModalOption: (modalOption: number | undefined) => void
  params: URLSearchParams
  setParams: SetURLSearchParams
}

export type AdminAssetProps = {
  loggedInUser: User
  paginatedUserTrackShareData: PaginatedData<UserTrackShareData>
  paginatedUserYoutubeVideoShareData: PaginatedData<UserYoutubeVideoShareData>
  selectedUser: User
  fetchUserTrackShareData: (
    id: number,
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => Promise<void>
  fetchUserYoutubeVideoShareData: (
    id: number,
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => Promise<void>
  selectTrackShareData: (data: UserTrackShareData | null) => void
  selectYoutubeVideoShareData: (data: UserYoutubeVideoShareData | null) => void
  deleteTrackShareData: (shareDataId: number) => void
  deleteYoutubeVideoShareData: (shareDataId: number) => void
  selectedTrackShareData: UserTrackShareData
  selectedYoutubeVideoShareData: UserYoutubeVideoShareData
  selectCollaboratorData: (data: CollaboratorData | null) => void
  selectedCollaboratorData: CollaboratorData | null
  tab: number
  setTab: (tab: number) => void
  modalOption: number | undefined
  setModalOption: (modalOption: number | undefined) => void
  params: URLSearchParams
  setParams: SetURLSearchParams
  selectUser: (user: User) => void
}

export class AdminState {
  paginatedUserTrackShareData = new PaginatedData<UserTrackShareData>()
  paginatedUserYoutubeVideoShareData = new PaginatedData<UserYoutubeVideoShareData>()
  paginatedUsers = new PaginatedData<User>()
  selectedUser: User | null = null
  selectedUserExpense: UserExpense | null = null
  adminPanelUserId: number | undefined = undefined
  selectedTrackShareData = new UserTrackShareData()
  selectedYoutubeVideoShareData = new UserYoutubeVideoShareData()
  lastUpdate: string | null
  summaries = new AnalysisSummaries()
  trackShareData: CollaboratorData[] = []
  selectedCollaboratorData = new CollaboratorData()
  affiliateFromList: Affiliate[] = []
  affiliateToList: Affiliate[] = []
  missingTrackShareHolderCount: number = 0
  royaltySummaryDuplicateCount: number = 0
  releaseEditAgainSubmitCount: number = 0
}

export type Role = {
  id: number | undefined
  name: string
}

export type MasterTemplateProps = {
  footer?: boolean
}

export type PaginationProps = {
  hidden: boolean
  pageCount: number
  currentPage: number
  onClickPage: (pageNumber: number) => void
  size?: "sm" | "md" | "lg"
}

export class UserForm {
  id: number | undefined = undefined
  username = ""
  password = ""
  firstName = ""
  lastName = ""
  roleId: number
  permissionIds: number[]
  lcUsername = ""
  lcPassword = ""
  sendEmail = false
  bmwCustomId = ""
  validateEmail = true
  genre = ""
  company = ""
  artistSpotifyId = ""
  excludeFromProjection = false
  hideReportTotalAmount = true
}

export interface SongOffer {
  title: string
  isrc: string
  offer: string
}

export interface SongMetadata {
  key: string
  value: string
}

export interface ConfirmedOffer {
  id: number
  spotifyId: string
  created: string
  advanceOffer: number
  buyoutOffer: number
  defaultBuyout: number
  defaultAdvance: number
  defaultAdvance2: number
  approved: boolean
  artist: Artist
  user: User
  userApproved: boolean
  confirmedTotal: number
  type: string
  songCount?: number
  offerCount?: number
  spotifyCount?: number
  splitRate?: number
  reportLtmIncome?: number
  title?: string
  trendThree?: number
  trendSix?: number
  trendTwelve?: number
  dealOffers: DealOffer[]
  currency?: string
  reportCurrencyConversion: number
}

export interface DealOffer {
  id?: number
  confirmedOffer?: ConfirmedOffer
  confirmedOfferId?: number
  offer?: number
  version?: number
  type?: string
  status?: string
  currency?: string
  songOffers?: string
  newReleaseInclude?: boolean
  newReleaseCommissionRate?: string
  newReleasePostCommissionRate?: string
  newReleaseIncludeRecoup?: boolean
  contractDuration?: string
  contractPreRecoupCommission?: string
  contractPostRecoupCommission?: string
  contractExclusivity?: boolean
  approved?: boolean
  description?: string
  changes?: string
  userId?: number
  user?: User
  created?: string
  signedAt?: string
  songOfferList?: SongOffer[]
  songChangeList?: SongOffer[]
  metadataChangeList?: SongMetadata[]
  recoupBase?: string
  catalogDefinition?: string
  syncRights?: boolean
  payoutFrequency?: string
  payoutThreshold?: string
  payoutCurrency?: string
  scoutUser?: string
}

export class UserExpenseForm {
  id?: number | undefined = undefined
  adminFee: number = 5
  distributionFee: number = 30
  distributionFeePostRecoup: number = 0
  user: number | undefined = undefined
  taxType: number = 1
  preRecoupCommission: number = 0
  postRecoupCommission: number = 0
  preferredCurrency: number = 1
  paymentPeriod: number = 1
  publishingKahunaShare: number = 0
  paymentThreshold: number = 0
}

export type HeaderProps = {
  fetchUserData: () => Promise<void>
  loggedIn: boolean
  user: User
  newReleasePermission: boolean
  getAdavencePermission: boolean
  appliedAffiliateUser: User | undefined
}

export class ShareRole {
  id: number | undefined = undefined
  name = ""
  share?: number
}

export class UserTrackShareRole {
  id: number | undefined = undefined
  roleDetail = new ShareRole()
  role: number | undefined = undefined
  trackShareHolder: number | undefined = undefined
}

export class UserTrackShareData {
  id: number
  userDetail = new User()
  trackDetail = new Track()
  collaboratorList: ShareRole[] = []
  roleList: UserTrackShareRole[] = []
  title = ""
  share: ""
  track: number | undefined = undefined
  user: number | undefined = undefined
}

export class UserYoutubeVideoShareData {
  id: number
  userDetail = new User()
  roleList: UserTrackShareRole[] = []
  title = ""
  share: ""
  youtubeVideo: number | undefined = undefined
  user: number | undefined = undefined
  youtubeVideoDetail = new YoutubeVideo()
}

export type AdminTrackFormProps = {
  user: User
  shareData: UserTrackShareData
  saveTrackShareForm: (formValues: UserTrackShareForm) => void
  selectTrackShareData: (data: UserTrackShareData | null) => void
  creatingRef: MutableRefObject<boolean>
  open: boolean
  onClose: () => void
}

export type AdminYoutubeVideoFormProps = {
  user: User
  shareData: UserYoutubeVideoShareData | null
  saveYoutubeVideoShareForm: (formValues: UserYoutubeVideoShareForm) => void
  selectYoutubeVideoShareData: (data: UserYoutubeVideoShareData | null) => void
  creatingRef: MutableRefObject<boolean>
  open: boolean
  onClose: () => void
}

export class UserTrackShareForm {
  id: number | undefined = undefined
  user: number | undefined = undefined
  track: number | undefined = undefined
  share = ""
  title = ""
  mainOwner = false
  roles: number[] = []
}

export class UserYoutubeVideoShareForm {
  id: number | undefined = undefined
  user: number | undefined = undefined
  youtubeVideo: number | undefined = undefined
  share = ""
  title = ""
  roles: number[] = []
  trackIsrc = ""
  mainOwner = false
}

export type SearchSelectProps = {
  id?: string
  name?: string
  label?: string
  className?: string
  value?: any
  onChange?: ChangeEventHandler<HTMLInputElement>
  onTextChange?: ChangeEventHandler<HTMLInputElement>
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  disabled?: boolean
  options?: SelectOption[]
  placeholder?: string
  loading?: boolean
  multiple?: boolean
  onSelect?: ChangeEventHandler<HTMLInputElement>
  disabledOptions?: any[]
  inputTextClass?: string
  containerStyle?: CSSProperties
}

export type SelectProps = {
  id?: string
  name?: string
  label?: string
  className?: string
  style?: any
  value?: any
  onChange?: ChangeEventHandler<HTMLInputElement>
  disabled?: boolean
  options?: SelectOption[]
  placeholder?: string
  loading?: boolean
  onSelect?: (value: any) => void
  inputTextClass?: string
  buttonClass?: string
}

export type SelectOption = {
  value: string | number
  label: ReactNode
  label2?: string
  type?: string
  spotifyId?: string | number
  cover?: string
}

export type HttpMethods = "get" | "post" | "put" | "delete" | "patch"

export class AnalysisTotal {
  date = ""
  totalNet = 0
}

export class AnalysisTopSongs {
  changeQuantity = 0
  identifier = ""
  percentage = 0
  platform = ""
  quantity = 0
  track = ""
  videoId = ""
  releaseCover = ""
  artistList = []
}

export class AnalysisState {
  totals: AnalysisTotal[] = []
  topSongDates: string[] = []
  selectedDate: string = ""
  selectedFilter = ANALYSIS_FILTER.TOP_TRACKS
  summaries = new AnalysisSummaries()
  topSongs: AnalysisTopSongs[] = []
  filterOptions: { [key: string]: KSelectOption[] }
  allSummaries: AnalysisSummary[]
}

export type AnalysisSummaryListProps = {
  currency: string
  summaries: AnalysisSummaries
}

export class AnalysisSummary {
  [key: string]: any
  id: number | undefined = undefined
  label = ""
  currency = "TRY"
  quantity = 0
  totalAmount = 0
  totalNet: number = 0 // Revenue Total
  totalNetAll: number = 0
  user = ""
  date = ""
  approvedDate = ""
  paymentDate = ""
  totalGross: number = 0
  userName = ""
  totalApproved: number = 0 // Paid Total
  advanceTotal: number = 0
  totalNetCurrency = ""
  advanceCurrency = ""
  userCompany = ""
  paymentPeriodName?: string
  taxTypeName?: string
  paidTotal: number = 0
  paymentThreshold: number = 0
  revenueTotalAllApproved: number = 0
  revenueApprovedDateList: string[]
  photo?: string
  totalApprovedDetails?: { advance: number; regular: number }
  approved: boolean
  paymentApproved: boolean
  paymentPeriodId: number
  transferDate?: string
  approveCount?: number
  paymentApproveCount?: number
  totalCount?: number
  affiliateTotal: number = 0
  affiliatePaid: number = 0
  monthlyList?: AnalysisSummary[]
  paymentDayList?: string[]
  kahunaReceivedAmount: number = 0
}

export type UserBalanceTotal = {
  currency: string
  totalNet: number // Revenue Total
  totalApproved: number // Paid Total
}

export class ReportsApprovePaymentRequest {
  userId: number
  date: string[]
  paymentDay: string
  deductFromAdvance: boolean
  payWithRevolut?: boolean
}

export class AnalysisSummaries {
  platform: AnalysisSummary[] = []
  storeFront: AnalysisSummary[] = []
  topTracks: AnalysisSummary[] = []
  allPlatformTotal: AnalysisSummary[] = []
  allAccounts: AnalysisSummary[] = []
  userBalance: AnalysisSummary[] = []
  count: number
}

export type AnalysisTotalsProps = {
  totals: AnalysisTotal[]
  fetchTotals: () => Promise<void>
  fetchFilterOptions: () => Promise<void>
  fetchAllSummaries: (dateKey: string, type: string, value: string | number, affiliate: boolean) => Promise<void>
  filterOptions: { [key: string]: KSelectOption[] }
  allSummaries: AnalysisSummary[]
  selectDate: (date: string) => void
  selectedDate: string
  currency: string
}

export type AnalysisChartProps = {
  totals: AnalysisTotal[]
  currency: string
  selectedDate: string
  selectFilter: (filter: string) => void
  //summaries: AnalysisSummaries
  selectedFilter: string
  fetchFilterOptions?: () => Promise<void>
  dateKey: string
  fetchAllSummaries: (
    dateKey: string,
    type: string,
    value: string | number,
    affiliate: boolean,
    startDate?: string,
    endDate?: string
  ) => Promise<void>
  //allSummaries: AnalysisSummary[]
  initialData: AnalysisSummary[]
  filterOptions?: { [key: string]: KSelectOption[] }
  user?: any
  monthRange: number
  defaultStartDate: Date
  defaultEndDate: Date
}

export type AffiliateChartProps = {
  totals: AnalysisTotal[]
  currency: string
  selectedDate: string
  selectFilter: (filter: string) => void
  //summaries: AnalysisSummaries
  selectedFilter: string
  fetchFilterOptions?: () => Promise<void>
  fetchAllSummaries: (
    dateKey: string,
    type: string,
    value: string | number,
    affiliate: boolean,
    startDate?: string,
    endDate?: string
  ) => Promise<void>
  allSummaries: AnalysisSummary[]
  //initialData: AnalysisSummary[]
  filterOptions?: { [key: string]: KSelectOption[] }
  user?: any
}

export type TooltipProps = {
  place?: "top" | "bottom" | "left" | "right"
  content?: ReactNode
  type?: "info" | "warn" | "error"
  width?: number
  disableBackground?: boolean
  backgroundColor?: string
}

export type ReportsProps = {
  isAdmin: boolean
  paymentHistory: PaymentHistoryDetail[]
  user: User
  fetchReportsData: (userId?: number, approveDisabled?: boolean, selectedAccount?: string) => Promise<any>
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}

export type ReleaseSubmissionProps = {
  isAdmin: boolean
  user: User
}

export type RoyaltiesProps = {
  isAdmin: boolean
  user: User
}

export type AccountProps = {
  user: User
  selected: string
  fetchUserData: () => Promise<void>
}

export class ReportsData {
  paymentHistory: PaymentHistoryDetail[] = []
}

export class UsersState {
  socialMediaList: SocialMedia[] = []
  eventList: Event[]
}

export type RequestConfig = {
  path: string
  method: Method
  data?: any
}

export class CollaboratorData {
  invitation_id: number
  first_name: string
  last_name: string
  email: string
  photo: string | null
  roles: string[]
  role_ids: number[]
  share: string = ""
  status: number
}

export type TrackShareHolderTableProps = {
  user: User
  trackShareData: CollaboratorData[]
  fetchTrackShareData: (trackId: number) => Promise<any>
  trackId: number
}

export type UserAdvanceTotals = {
  advanceCount: number
  advanceAverage: number
  advanceRecoupment: number
}

export interface UserAdvanceProgress {
  userId: number
  user: User
  currency: string
  advances: { [key: string]: number }
  royalties: { [key: string]: number }
  projections: { [key: string]: number }
  irrAnnual: number
  irrMonthly: number
  irr12M: number // 12-month IRR (Forecast)
  irr24M: number // 24-month IRR (Forecast)
  irr36M: number // 36-month IRR (Projection)
  recoupMonthTotal: number
  recoupActualTotal: number
  recoupProjectionTotal: number
  newUser: boolean
}

export type PerformanceStats = {
  totalArtists: number
  newArtists31d: number
  totalTracks: number
  newTracks31d: number
  takedownTracks: number
  newTakedownTracks31d: number
  totalStreams: number
  newStreams31d: number
  totalAdvances: number
  newAdvances31d: number
  actualAvgRecoupMonths: number
  estimatedAvgRecoupMonths: number
  totalAvgRecoupMonths: number
  totalReleases: number
  newReleases31d: number
  takedownReleases: number
  newTakedownReleases31d: number
  totalUsers: number
  newUsers31d: number
}

export interface UserInformation {
  id?: number
  type: string
  name: string
  address: string
  country: string
  birthday: string
  taxId?: string
  companyRegistrationNo?: string
}

export type UserTakedown = {
  id: number
  userId: number
  user: User
  userMembers: User[]
  assetDrive: string
  albumIds: string
  created: string
  authenticatedLc: boolean
  labelCreated: boolean
  contractSigned: boolean
  artistUploadStatus: string
  takedownMetadataStatus: string
  takedownAssetStatus: string
  dealId: number
  confirmedOfferId: number
  takedownMetadataProgress: number
  takedownAssetProgress: number
  userInformation?: UserInformation
  takedownAssetUploadResult?: string
  takedownAssetUploadSuccess?: string
  takedownAssetUploadFail?: string
  lastAssetUploadFilename?: string
}

export type UserLog = {
  id: number
  userId?: number
  user?: User
  created: string
  log: string
  type: string
  release?: Release
  releaseId?: number
}

export type TakedownLog = {
  id: number
  takedownFinished?: boolean
  user?: User
  status: string
  log: string
  progress: string
  created?: string
  createdBy?: User
  releaseCount?: number
  spotifyVisibleCount?: number
  spotifyCompleteCount?: number
  spotifySplitExistCount?: number
  appleVisibleCount?: number
  appleCompleteCount?: number
  youtubeVisibleCount?: number
  youtubeCompleteCount?: number
}

export type ReleaseTakedownLog = {
  id: number
  takedownLog?: TakedownLog
  takedownLogId?: number
  release?: Release
  status: string
  log: string
  spotifyId: string
  linkedSpotifyIds?: string
  spotifyLinkCount?: number
  spotifyVisible?: boolean
  spotifyComplete?: boolean
  spotifySplitExist?: boolean
  appleId: string
  linkedAppleIds?: string
  appleLinkCount?: number
  appleVisible?: boolean
  appleComplete?: boolean
  youtubeLinkCount?: number
  youtubeVisible?: boolean
  youtubeComplete?: boolean
}

export type TakedownAlbum = {
  releaseDate: string
  upc: string
  distributor: string
  title: string
}

export type TakedownTrack = {
  id: number
  ltm: number
  albumId: number
  title: string
  isrc: string
  album: TakedownAlbum
  songSelection: SongSelection
  artists: Artist[]
}

export type SongSelection = {
  id: number
  advanceOffer: number
  isrc: string
  ltmIncome: number
  ownership: number
}

export type PlatformRoyalty = {
  [key: string]: number
}

export type CountryRoyalty = {
  [key: string]: number
}

export type TakedownBatch = {
  trackList: TakedownTrack[]
  platformRoyalties: PlatformRoyalty
  countryRoyalties: CountryRoyalty
  currency: string
  sheetValues?: string[][]
  artistId: number
  missingPlatforms: string | null
  missingPlatformsRisk: string[]
  missingPlatformsOpportunity: string[]
  unsupportedPlatforms: string | null
}

export class Currency {
  name = ""
}

export class CurrencyExchange {
  id: number
  exchangeRates: number | undefined = undefined
  currencyTypeDetail = new Currency()
  date: string
  currencyId: number
  created: string
}

export type AdminTrackShareHolderTableProps = {
  user: User
  trackShareData: CollaboratorData[]
  fetchTrackCollaboratorData: (trackId: number) => Promise<any>
  trackId: number
  selectCollaboratorData: (data: CollaboratorData | null) => void
}

export type AdminCollaboratorFormProps = {
  selectedCollaboratorData: CollaboratorData | null
  saveCollaboratorForm: (formValues: AdminCollaboratorShareForm) => void
  selectCollaboratorData: (data: CollaboratorData | null) => void
  track_id: number
  open: boolean
  onClose: () => void
}

export class AdminCollaboratorShareForm {
  email: string | undefined = undefined
  share: string = ""
  roles: number[] = []
  track_id: number
  name: string
}

export class DownloadReportButtonProps {
  reportId: string
  user: User
  downloadReport: (quarter: string, userId?: number, approveDisabled?: boolean) => Promise<string[]>
  isAdmin?: boolean
  selectedUserId?: number
  approveDisabled?: boolean
}

export type InvitationStatus = {
  message: string
  registered: boolean
  status: INVITATION_STATUS
}

export type InvitationAcceptProps = {
  acceptInvitation: (token: string) => Promise<void>
  invitationStatus: InvitationStatus
}

export type InvitationLayoutProps = {
  title?: string
  text?: string
  showLoginButton?: boolean
  children?: ReactNode
}

export type AnalysisTopSongsProps = {
  date: string
  fetchTotals: () => Promise<void>
  fetchTopSongs: (date: string) => Promise<void>
  fetchTopSongDateOptions: (date: string) => Promise<void>
  topSongDates: string[]
  topSongs: AnalysisTopSongs[]
  dateOptions: { date: string; totalNet: number }[]
  analysis: any
}

export type Scheduler = {
  id: number
  name: string
  description: string
  next_run: string
  previous_run: string
  enabled: boolean
  cron: string
  type: string
  log: boolean
  value: string
  report_list: SchedulerReport[]
}

export type Registration = {
  calendly_id: string
  start_time: string
  calendly_status: string
  db_status: string
  description: string
  created_at: string
  name: string
  email: string
  phone_number: string
  country: string
  role: string
  artists: string
  hearing_source: string
}

export type AssetRegistryState51BatchId = {
  id: number
  batchId: string
  releaseId: number
}

export type AssetRegistryUpcCodes = {
  id: number
  upc: string
  releaseId: number
}

export type AssetRegistryIsrcCodes = {
  id: number
  isrc: string
  releaseId: number
}

export type TerritoryDsp = {
  id: number
  label: string
  image: string
}

export type AssetRegistryTrackShareHolder = {
  id?: number
  name: string
  email: string
  share: string
  isrc: string
  roles: string
}

export type AssetRegistryPublishingShareHolder = {
  id?: number
  name: string
  share: string
  isrc: string
  role: number
  mainOwner: boolean
}

export type TrackRegistryConsentFile = {
  id?: number
  title: string
  track_isrc: string
  release_id: number
  file_url: string
  asset_id: string
}

export type SchedulerReport = {
  id: number
  scheduler_id: number
  date: string
  finished_date?: string
  status: string
  value: string
  _read: number
  _warning: number
  _error: number
  _insert: number
  _update: number
  _delete: number
  user_id: number
  log_file?: string
  log_content?: string
}

export type Affiliate = {
  id?: number
  fromUser?: User
  toUser?: User
  fromUserId: number
  toUserId: number
  commission: number
}

export type PublicSmartLink = {
  release?: Release
  track?: Track
  smartLink?: SmartLink
  preSave?: boolean
}

export type ArtistSmartLink = {
  id?: number
  userId?: number
  viewCount?: number
  link?: string
  user?: User
  socialMediaList?: SocialMedia[]
  releaseList?: Release[]
  eventList?: Event[]
}

export interface CustomFile {
  files: FileList
}

export type RotorVideoOutput = {
  id: number
  title: string
  duration: number
  video_url: string
  style_id: number
  variant_id: number
  progress: number
  progress_message: string
  aspect_ratio: string
}

export type RotorVideoAttributes = {
  video_id: number
  outputs: RotorVideoOutput[]
}

export type RotorVideo = {
  attributes: RotorVideoAttributes
}

export type RotorStyleVariant = {
  id: number
  duration: number
  aspect_ratio: string
  preview: string
  title: string
}

export type RotorStyleAttributes = {
  id: number
  preview: string
  title: string
  variants: RotorStyleVariant[]
}

export type RotorStyle = {
  attributes: RotorStyleAttributes
}

export type Contract = {
  id: number
  created: string
  type: string
  url: string
  userId: string
  signed: boolean
  blob?: any
}

export interface InformationPageForm {
  contactName: string
  country: string
  email: string
  phoneNumber: string
  code: string
  ipAddress: string
}

export type UserNotification = {
  param1: string
  param2: string | undefined
  param3: string | undefined
  read: boolean
  readOn: string | undefined
  readAt: string | undefined
  id: string | number
  userId: number | undefined
  created: string
  buttonParam: string | undefined
  urlParam: string | undefined
  relatedUrl: string | undefined
  notificationId: number // notification type id
}

export type Announcement = {
  id: number
  title: string
  descriptionEn: string
  descriptionTr: string
  descriptionDe: string
  startDate?: string | null
  endDate?: string | null
  created?: string
}

export type AdminUserAnnouncement = {
  id: number
  userId: number | undefined
  announcementId: number
  title: string
  descriptionEn: string
  descriptionTr: string
  read: boolean
  readOn: string | undefined
  readAt: string | undefined
  created: string
}

export type GroupedNotifications = {
  [key: string]: { notifications: UserNotification[]; unreadCount: number }
}

export class UserNotificationsState {
  unreadNotificationsNumber: number
  paginatedNotifications: PaginatedData<UserNotification> = new PaginatedData<UserNotification>()
}

export class RotorVideoAuthConfig {
  accessToken: string
  clientId: string
}

export class RotorVideoTrack {
  id: string
  providerName: string
  artistName: string
  releaseId: string
  releaseType: string
  releaseName: string
  releaseArtworkUrl: string
  trackName: string
  artworkUrl: string
  audioUrl: string
}

export class RotorVideoOption {
  authConfig: RotorVideoAuthConfig
  availableTracks: RotorVideoTrack[]
}

export type AdminCatalogTableColumnType = {
  name: string
  visible: boolean
}

export type UploadOptionsDropdownType = {
  onSubmitLabelcamp: () => void
  onSubmitBmv: () => void
  onSubmitMesamMsg: () => void
  onSubmitEditorialPitching: () => void
  onSubmitState51: () => void
  onSubmitMarketing: () => void
}

export type AdminCatalogSelectedActionParams = {
  type: "labelCampUpdate" | "statusUpdate" | "releaseDelete" | "editStatusUpdate" | undefined
  id: number | undefined
  status: string | undefined
  editStatus: string | undefined
  title: string | undefined
  checked: boolean
}

export type AdminUserMenuProps = {
  setModal: (modalOption: number | undefined) => void
  fetchUserData: () => Promise<void>
  user: User
  authUser: User
  affiliateFromList: Affiliate[]
  affiliateToList: Affiliate[]
  userDetailPage?: boolean
  canWrite: boolean
}

export type AdminUserModalsContainerProps = {
  user: User
  modalOption: number | undefined
  setModalOption: (modalOption: number | undefined) => void
  fetchAffiliateList: (userId: number) => Promise<void>
  affiliateFromList: Affiliate[]
  affiliateToList: Affiliate[]
  paymentMethodsAdminEntry: (adminPanelUserId?: number) => void
  selectedUserExpense: UserExpense
  fetchUserExpenseData: (userId: number) => Promise<void>
  saveUserExpenseData: (formValues: UserExpenseForm) => Promise<void>
  loggedInUser: User
  fetchUserData: () => Promise<void>
  selectUser: (user: User | null) => void
  applyUserExpenseToPreviousMonths: (userExpenseId: number, months: string) => Promise<void>
}

export class TrackUploadRequest {
  title: string
  isrc: string
  fileId: string
  productId: string
  trackId: string
  downloadUrl: string
  extension: string
}

export class Playlist {
  playlistId: string
  description: string
  name: string
  platform: string
  tags: string
  moods: string
  followers: number
  fdiffWeek: number
  fdiffMonth: number
  image: string
}

// indicates the Track and Playlist pairs. (Track which is included in any playlist (only Spotify for now) => Track and Playlist details )
export class TrackPlaylist {
  id: number
  addedAt: string
  removedAt: string | null
  isrc: string
  track: AdminTrack
  updated: string
  created: string
  cmSysLastUpdated: string
  playlist: Playlist
  position: number
  peakPosition: number
  trackId: number
  playlistId: number
  currentStorefronts: string | null
  removedStorefronts: string | null
}

export type SummaryTotals = {
  openingBalance: number
  newEarnings: number
  advanceRecouped: number
  advanceRemaining: number
  paidOut: number
  currentBalance: number
  currency: string
  paymentDate: string
  advanceTotal: number
}

export type SummaryBalance = {
  dsp: string
  activityPeriod: string
  reportingPeriod: string
  currency: string
  quantity: number
  totalNet: number
}

export type UpcomingTotals = {
  estimatedRevenue: number
  progress: number
  currency: string
  paymentDate: string
}

export type SummaryUpcoming = {
  dsp: string
  activityPeriod: string
  reportingPeriod: string
  currency: string
  quantity: number
  totalNet: number
  paymentDay?: string
  paymentDate?: string
  paymentType?: string
  description?: string
}

export type SummaryStatements = {
  date: string
  currency: string
  paymentDay: string
  paymentType: string
  paidAmount: number
  description: string
  paymentBatchBatchId: string | null
  transactionId: string | null
}

export class TrendDemographic {
  id: number
  trendCountryId: number
  sex: string
  streams: number
  ageGroup: string
}

export class TrendCountry {
  id: number
  trendTrackId: number
  country: string
  streams: number
  trendDemographicList?: TrendDemographic[]
}

export class TrendTrack {
  id: number
  isrc: string
  date: string
  streams: number
  skips: number
  saves: number
  spotifyId: string
  prevWeekStreams?: number // from now on streams will be used weekly in most cases. In some cases we will fetch previous week streams to compare
  track?: Track
  trendCountryList?: TrendCountry[]
  platform: string
  eventType?: string
}

export class TrendTopTrack {
  id: number
  isrc: string
  date: string
  streams: number
  streamChange: number
  position: number
  track?: AdminTrack
}

export class StreamTrackMonth {
  id: number
  isrc: string
  streamsThisMonth: number
  streamsLastMonth: number
  streamsThreeMonths: number
  total: number
  track?: AdminTrack
}

export class ArtistTrendEconomy {
  userId?: number
  user?: User
  date?: string
  currency?: string
  spUnitPrice = 0
  grossUnitPrice = 0
  netUnitPrice = 0
  streams30 = 0
  streams60 = 0
  streams90 = 0
  streams30NewRelease = 0
  streams60NewRelease = 0
  streams90NewRelease = 0
  streams30BackCatalog = 0
  streams60BackCatalog = 0
  streams90BackCatalog = 0
  streams30Takedown = 0
  streams60Takedown = 0
  streams90Takedown = 0
  totalNet30: number = 0
  totalNet60: number = 0
  totalNet90: number = 0
  totalAmount30: number = 0
  totalAmount60: number = 0
  totalAmount90: number = 0
  totalNet30NewRelease: number = 0
  totalNet60NewRelease: number = 0
  totalNet90NewRelease: number = 0
  totalNet30BackCatalog: number = 0
  totalNet60BackCatalog: number = 0
  totalNet90BackCatalog: number = 0
  totalNet30Takedown: number = 0
  totalNet60Takedown: number = 0
  totalNet90Takedown: number = 0
  totalAmount30NewRelease: number = 0
  totalAmount60NewRelease: number = 0
  totalAmount90NewRelease: number = 0
  totalAmount30BackCatalog: number = 0
  totalAmount60BackCatalog: number = 0
  totalAmount90BackCatalog: number = 0
  totalAmount30Takedown: number = 0
  totalAmount60Takedown: number = 0
  totalAmount90Takedown: number = 0
  royaltyType?: string
  country?: string
  platform?: string
  streamsPlatform?: string
}

export type StreamsChartData = {
  month: string
  monthLabel: string
  streams: number
}

export type StreamsTrackMonthsData = {
  tracks: StreamTrackMonth[]
  chartData: StreamsChartData[]
  totalStreams: number
  missingDays: string[]
}

export type RoyaltyChartData = {
  month: string
  monthLabel: string
  royalty: number
  streams: number
}

export type ArtistTrendEconomyData = {
  totals: ArtistTrendEconomy[]
  results: ArtistTrendEconomy[]
  chartData: RoyaltyChartData[]
  totalRoyalty: number
  totalStreams: number
  missingDays: string[]
}

export class TrendsState {
  options: TrendFilterOption = {
    songOptions: [],
    countryOptions: [],
    sexOptions: [],
    ageGroupOptions: [],
    artistOptions: []
  }
  streamsDaily: TrendTrack[] = []
  streamsCountry: TrendTrack[] = []
  streamsDemographic: TrendTrack[] = []
  trendTopTracks: TrendTopTrack[] = []
  streamsTrackMonths: StreamsTrackMonthsData = { tracks: [], chartData: [], totalStreams: 0, missingDays: [] }
  artistTrendEconomyData: ArtistTrendEconomyData = { results: [], totals: [], chartData: [], totalRoyalty: 0 }
}

export class TrendFilter {
  artistList: string[]
  platformList: string[]
  dateValues: DateRangeType
  isrcList: string[]
  countryList: string[]
  sexList: string[]
  ageGroupList: string[]
  selectedUsers?: number[]
  eventType: string
  isAdmin: boolean = false
}

export class TrendFilterOption {
  songOptions?: Track[]
  platformOptions?: string[]
  countryOptions?: string[]
  sexOptions?: string[]
  ageGroupOptions?: string[]
  artistOptions?: Artist[]
}

export type TrendsProps = {
  options: TrendFilterOption
  streamsDaily: TrendTrack[]
  fetchStreamsDaily: (filter: TrendFilter, fetchOptions: boolean) => Promise<void>
  streamsCountry: TrendTrack[]
  fetchStreamsCountry: (filter: TrendFilter, fetchOptions: boolean) => Promise<void>
  streamsDemographic: TrendTrack[]
  fetchStreamsDemographic: (filter: TrendFilter, fetchOptions: boolean) => Promise<void>
  trendTopTracks: TrendTopTrack[]
  fetchTrendTopTracks: (date: string, fetchOptions: boolean, filter: TrendFilter) => Promise<void>
  streamsTrackMonths: StreamsTrackMonthsData
  fetchStreamsTrackMonths: (
    filter: TrendFilter,
    offset: number,
    limit: number,
    selectedRoyaltyType?: KSelectOption
  ) => Promise<void>
  artistTrendEconomyData: ArtistTrendEconomyData
  fetchArtistTrendEconomyData: (
    filter: TrendFilter,
    offset: number,
    limit: number,
    selectedRoyaltyType?: KSelectOption
  ) => Promise<void>
  isAdmin: boolean
  user?: User
  externalTab?: string // to use DailyTrends in new Analytics
  hideSideBarAndTitle?: boolean
  commonDateValues?: DateRangeType
}

export type LabelBeatportLogo = {
  id?: string
  created?: string
  label?: string
  cover?: string
}

export type LabelBeatport = {
  value?: string
  label?: string
  type?: string
  labelBeatportLogo?: LabelBeatportLogo
}

export class InsightsData {
  paginatedTrackPlaylists: PaginatedData<TrackPlaylist> = new PaginatedData<TrackPlaylist>()
}
